import { AccountLevelArkchatPermission } from "./constant";

// "All" (every workspace at once) or the selected workspace. Which one is shown
// is this browser's choice; the account's active workspace on the server stays
// real, since creating groups, invites, roles and billing act on it.
const STORAGE_KEY = "arkchat.allWorkspaces";

export const readAllWorkspaces = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

export const saveAllWorkspaces = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Not remembered in this browser (private window, blocked storage).
  }
};

// The chats to list: each once, and with a workspace selected only its groups
// and the DMs with people in it (the server says which: workspaceIds). Without
// that (a list saved before it was sent, a group just created, a failed lookup)
// a group goes by its own account, and a DM stays listed rather than vanishing.
export const chatsInView = (list, allWorkspaces, activeWorkspaceId) => {
  const seen = new Set();
  return (list || []).filter((item) => {
    if (item?.pnId) {
      if (seen.has(item.pnId)) return false;
      seen.add(item.pnId);
    }
    if (allWorkspaces || !activeWorkspaceId) return true;
    if (Array.isArray(item?.workspaceIds)) return item.workspaceIds.includes(activeWorkspaceId);
    return item?.isGroup ? item.subscriberId === activeWorkspaceId : true;
  });
};

// A list of groups from elsewhere (it doesn't say which workspace they're listed
// under), narrowed by the same rule: the chat lists know, for anyone who joined
// a group through a collaborator. Otherwise a group goes by its own account.
export const groupsInView = (groups, chats, allWorkspaces, activeWorkspaceId) => {
  if (allWorkspaces || !activeWorkspaceId) return groups;
  const known = new Map((chats || []).filter((chat) => chat?.isGroup).map((chat) => [chat.id, chat]));
  return (groups || []).filter((group) => {
    const chat = known.get(group?.id);
    return chat ? chatsInView([chat], false, activeWorkspaceId).length > 0 : group?.subscriberId === activeWorkspaceId;
  });
};

const isInternal = (member) => member?.inviteType === 0 || member?.inviteType === 1;
const isTeamMember = (member) => isInternal(member) || member?.inviteType === 2;

// Every workspace's team as one contact list, each person once: under Internal
// if they're internal anywhere. `workspaces` keeps their entry in each one, for
// showing where they are and for acting on them in one of those workspaces.
export const mergeWorkspaceTeams = (teams, myUserId) => {
  const people = new Map();
  for (const team of teams) {
    const mine = team.members.find((member) => member?.userId === myUserId);
    const iAmAdmin = mine?.permission === AccountLevelArkchatPermission?.ADMIN;
    for (const member of team.members) {
      if (!isTeamMember(member)) continue;
      const workspace = { id: team.id, name: team.name, entry: member, iAmAdmin };
      const known = people.get(member.userId);
      if (!known) {
        people.set(member.userId, { ...member, workspaces: [workspace] });
      } else {
        const workspaces = [...known.workspaces, workspace];
        people.set(
          member.userId,
          isInternal(member) && !isInternal(known) ? { ...member, workspaces } : { ...known, workspaces },
        );
      }
    }
  }
  const everyone = [...people.values()];
  return {
    internalMember: everyone.filter(isInternal),
    externalMember: everyone.filter((person) => !isInternal(person)),
  };
};

export const workspaceNames = (person) =>
  (person?.workspaces || []).map((workspace) => workspace.name).filter(Boolean).join(", ");

export const allWorkspacesSelector = (state) => state?.appStateData?.allWorkspaces;
